import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Toolbar, Typography, useTheme
} from '@mui/material';
import { bindActionCreators } from '@reduxjs/toolkit';
import { getCurrentUser } from 'aws-amplify/auth';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { login } from '../state/account/action_creator';
import { updateSelectedSystem } from '../state/system/action_creator';
import { State } from '../state/reducers';
import { Account, System } from '../state/types';
import { fetchSystems } from '../state/system/action_creator';
import RoleBasedAccess, { userHasAccess } from '../services/RoleBasedAccess';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SystemSelectorModal from './SystemSelectorModal';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LogoutIcon from '@mui/icons-material/Logout';

interface NavbarProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ darkMode, toggleDarkMode }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const userData: Account = useSelector((state: State) => state.accountState.user)
  const { selectedSystem, systems } = useSelector((state: State) => state.systemState);
  const updateSelectedSystemAction = bindActionCreators(updateSelectedSystem, dispatch)
  const loginAction = bindActionCreators(login, dispatch)
  const fetchSystemsAction = bindActionCreators(fetchSystems, dispatch)

  const [open, setOpen] = useState(false)

  useEffect(() => {
    fetchSystemsAction();

    const savedSystem = localStorage.getItem('selectedSystem');
    if (savedSystem && !selectedSystem) {
      updateSelectedSystemAction(savedSystem);
    }
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then((currentUser) => {
        if (!currentUser) {
          navigate('/logout')
        } else if (currentUser.userId !== userData?.username) {
          loginAction(currentUser.userId)
        }
      })
      .catch(() => {
        navigate('/login')
      })
  }, [])

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: System) => sys.systemCode === systemCode)?.commonName
  }

  const clearVehicleNavStateAndNavigate = (path: string) => {
    sessionStorage.removeItem('vehiclesNavState');
    navigate(path);
  };


  const SpiderLogo: React.FC<{ darkMode: boolean, toggleDarkMode: () => void, loggedIn?: boolean }> = ({ darkMode, toggleDarkMode, loggedIn }) => {
    return (
      <Box onClick={loggedIn ? (() => clearVehicleNavStateAndNavigate('/')) : toggleDarkMode} sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <img src={require(`../media/${darkMode ? 'black' : 'white'}-spider.png`)} alt="Logo" style={{ height: 50, width: 'auto' }} />
      </Box>
    )
  };

  const accessibleSystemCodes = userHasAccess(userData?.role, ['Internal'])
    ? (systems || []).map((sys: System) => sys.systemCode)
    : userData?.system_access || [];

  if (!userData?.username || (location.pathname === '/logout')) {
    return (
      <AppBar position="static" sx={{ backgroundColor: theme.palette.primary.main, boxShadow: 'none' }}>
        <Toolbar sx={{ minHeight: 64, display: 'flex', justifyContent: 'center' }}>
          <SpiderLogo darkMode={darkMode} toggleDarkMode={toggleDarkMode} loggedIn={false} />
        </Toolbar>
      </AppBar>
    )
  }

  return (
    <AppBar position="fixed" sx={{ backgroundColor: theme.palette.primary.main, boxShadow: 'none', zIndex: 1000 }}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => clearVehicleNavStateAndNavigate('/')}
          >
            <SpiderLogo darkMode={darkMode} toggleDarkMode={toggleDarkMode} loggedIn={true} />
            <Typography
              variant="h6"
              color={theme.palette.text.primary}
              marginX={theme.spacing(3)}
            >
              BWI
            </Typography>
          </Box>
          <RoleBasedAccess allowedRoles={["Internal", "Client"]}>
            <Button
              onClick={() => clearVehicleNavStateAndNavigate('/vehicles')}
              sx={{
                color: `${theme.palette.text.primary}`,
                fontSize: '14px',
                width: '100px',
                minHeight: '60px !important',
                marginX: '5px',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              Vehicles
            </Button>
          </RoleBasedAccess>

          <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
            <Button
              onClick={() => clearVehicleNavStateAndNavigate('/systems')}
              sx={{
                color: `${theme.palette.text.primary}`,
                fontSize: '14px',
                width: '100px',
                minHeight: '60px !important',
                marginX: '5px',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              Systems
            </Button>

          </RoleBasedAccess>
        </Box>

        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 150,
              maxWidth: 500,
              textTransform: 'none',
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderColor: theme.palette.primary.light,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingRight: 1.5,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            endIcon={<ArrowDropDownIcon />}
          >
            <Typography
              variant="subtitle1" // Adjust as needed (e.g. 'body1', 'h6', etc.)
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 24px)', // leave room for icon
              }}
            >
              {getCommonNameForSystem(selectedSystem || '') || 'Select a System'}
            </Typography>
          </Button>

          <SystemSelectorModal
            open={open}
            onClose={() => setOpen(false)}
            onSelect={(systemCode) => {
              localStorage.setItem('selectedSystem', systemCode);
              updateSelectedSystemAction(systemCode);
              if (location.pathname !== '/systems') {
                navigate('/');
              }
            }}
            systems={systems}
            selectedSystem={selectedSystem || ''}
            accessibleSystemCodes={accessibleSystemCodes}
          />

          <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            <IconButton
              onClick={toggleDarkMode}
              sx={{
                color: theme.palette.text.primary,
                minWidth: '48px',
                minHeight: '48px',
                marginLeft: 2,
              }}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Manage Accounts">
            <IconButton
              onClick={() => clearVehicleNavStateAndNavigate('/account')}
              sx={{
                color: theme.palette.text.primary,
                marginLeft: 1,
                minWidth: '48px',
                minHeight: '48px',
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Logout">
            <IconButton
              onClick={() => clearVehicleNavStateAndNavigate('/logout')}
              sx={{
                color: theme.palette.text.primary,
                marginLeft: 1,
                minWidth: '48px',
                minHeight: '48px',
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  )
};

export default Navbar;
